import { apiClient } from "./client";

export type TickerSeverity = "critical" | "attention" | "info" | "good-news";
export type TickerSpeed = "slow" | "medium" | "fast";

export interface TickerItemDto {
  signalKey: string;
  severity: TickerSeverity;
  severityLabel: string;
  text: string;
  valueKind: string;
  isAiWritten: boolean;
  linkPath: string;
  score: number;
}

export interface TickerDto {
  fingerprint: string;
  generatedAtUtc: string;
  items: TickerItemDto[];
  totalSignals: number;
  speedDefault: TickerSpeed;
}

interface CachedTickerEntry {
  etag: string;
  data: TickerDto;
}

const cacheByScope = new Map<string, CachedTickerEntry>();

/** Clears the client-side ETag and last-data cache (used on sign-out or in unit tests). */
export function resetTickerApiCache(): void {
  cacheByScope.clear();
}

export interface FetchTickerOptions {
  /** Cache scope key (e.g. principal UPN + roles) so switching personas never reuses another role's ETag. */
  scopeKey?: string;
  /** Optional explicit ETag override. */
  ifNoneMatch?: string | null;
}

/**
 * Fetches `GET /api/v1/ticker` with `If-None-Match` conditional caching (D-O64, O-R38).
 * Allows HTTP 304 via `validateStatus` and returns the previously cached `TickerDto` when nothing changed.
 */
export async function fetchTicker(options?: FetchTickerOptions): Promise<TickerDto | null> {
  const scopeKey = options?.scopeKey ?? "default";
  const previous = cacheByScope.get(scopeKey);
  const etagToSend = options?.ifNoneMatch !== undefined ? options.ifNoneMatch : previous?.etag ?? null;

  const headers: Record<string, string> = {};
  if (etagToSend) {
    headers["If-None-Match"] = etagToSend;
  }

  const response = await apiClient.get<TickerDto>("/ticker", {
    headers,
    validateStatus: (status) => status === 200 || status === 304
  });

  if (response.status === 304) {
    return previous?.data ?? null;
  }

  const data = response.data;
  if (!data) {
    return previous?.data ?? null;
  }

  const headerEtag =
    typeof response.headers?.etag === "string" && response.headers.etag.trim().length > 0
      ? response.headers.etag.trim()
      : data.fingerprint
        ? `"${data.fingerprint}"`
        : "";

  if (headerEtag) {
    cacheByScope.set(scopeKey, { etag: headerEtag, data });
  }

  return data;
}

export const tickerApi = {
  getTicker: fetchTicker,
  resetCache: resetTickerApiCache
};
